<template>
  <div class="priv sa-scope">
    <div class="priv__wrap">
      <div class="priv__eyebrow">Sartor Inmobiliaria</div>
      <h1 class="priv__title">Política de Privacidad</h1>
      <p class="priv__updated">Última actualización: julio de 2026</p>

      <p class="priv__lead">
        En Sartor Inmobiliaria valoramos tu privacidad. Esta política explica qué datos
        personales recopilamos cuando usás nuestro sitio, con qué finalidad, con quién
        los compartimos y qué derechos tenés sobre ellos.
      </p>

      <section class="priv__sec">
        <h2>1. Responsable del tratamiento</h2>
        <p>
          <b>Sartor Inmobiliaria</b> — Habegger 1444, Reconquista y C. 12 N.º 677,
          Avellaneda, Provincia de Santa Fe, Argentina. Consultas sobre privacidad:
          <a href="mailto:privacidad@sartorinmobiliaria.com">privacidad@sartorinmobiliaria.com</a>
          o mediante nuestro <a href="/#contact-section">formulario de contacto</a>.
        </p>
      </section>

      <section class="priv__sec">
        <h2>2. Qué datos recopilamos</h2>
        <ul>
          <li><b>Datos que nos das</b>: cuando enviás una consulta, solicitás una tasación o dejás tus datos en el chat, recopilamos tu nombre, email y teléfono, junto con el mensaje o la propiedad de interés.</li>
          <li><b>Datos de tu cuenta</b>: si ingresás, guardamos tu email y, si usás Google, tu nombre y foto de perfil de Google.</li>
          <li><b>Actividad en el sitio</b>: búsquedas que hacés, propiedades que ves, favoritos que guardás y acciones similares. Esto nos permite mostrarte recomendaciones y mejorar el servicio.</li>
          <li><b>Datos técnicos</b>: un identificador propio de tu navegador (cookie <code>sartor_vid</code>), tipo de dispositivo y páginas visitadas.</li>
        </ul>
        <p>No recopilamos datos sensibles ni realizamos decisiones automatizadas con efectos legales sobre vos.</p>
      </section>

      <section class="priv__sec">
        <h2>3. Para qué usamos tus datos</h2>
        <ul>
          <li>Responder tus consultas y gestionar tu interés en propiedades.</li>
          <li>Mostrarte propiedades similares y recomendaciones relevantes.</li>
          <li>Sincronizar tus favoritos y búsquedas guardadas entre tus dispositivos cuando iniciás sesión.</li>
          <li>Entender el uso del sitio de forma agregada para mejorarlo.</li>
          <li>Enviarte, cuando corresponda, comunicaciones vinculadas a tu actividad (por ejemplo, el enlace de acceso a tu cuenta).</li>
        </ul>
      </section>

      <section class="priv__sec">
        <h2>4. Cookies y almacenamiento local</h2>
        <p>
          Usamos una cookie propia (<code>sartor_vid</code>) para reconocer tu navegador y
          ofrecer analítica y recomendaciones; una cookie de sesión (<code>sartor_auth</code>)
          cuando iniciás sesión; y almacenamiento local del navegador para recordar tus
          favoritos. No usamos cookies de publicidad de terceros. Podés borrar estas cookies
          desde tu navegador en cualquier momento.
        </p>
      </section>

      <section class="priv__sec">
        <h2>5. Inicio de sesión con Google</h2>
        <p>
          Si elegís “Ingresar con Google”, Google nos comparte tu email verificado, nombre y
          foto de perfil para crear o identificar tu cuenta. No accedemos a tu contraseña de
          Google ni a otros datos de tu cuenta. El uso que Google hace de tus datos se rige por
          su propia política de privacidad.
        </p>
      </section>

      <section class="priv__sec">
        <h2>6. Con quién compartimos tus datos</h2>
        <p>No vendemos tus datos personales. Los compartimos únicamente con proveedores que nos ayudan a operar, y solo en lo necesario:</p>
        <ul>
          <li><b>Google</b> — inicio de sesión con Google.</li>
          <li><b>Proveedor de correo</b> — para enviarte emails de acceso o respuesta.</li>
          <li><b>Proveedores de infraestructura</b> — alojamiento del sitio y las bases de datos.</li>
        </ul>
        <p>También podríamos compartir datos si nos lo exige una obligación legal.</p>
      </section>

      <section class="priv__sec">
        <h2>7. Conservación</h2>
        <p>Conservamos tus datos mientras mantengas interés o cuenta activa, y durante los plazos que exijan las obligaciones legales o comerciales aplicables. Luego los eliminamos o anonimizamos.</p>
      </section>

      <section class="priv__sec">
        <h2>8. Seguridad</h2>
        <p>Aplicamos medidas razonables para proteger tus datos. Ninguna transmisión por Internet es completamente segura, pero trabajamos para resguardar tu información.</p>
      </section>

      <section class="priv__sec">
        <h2>9. Tus derechos</h2>
        <p>
          De acuerdo con la Ley 25.326 de Protección de los Datos Personales, tenés derecho a
          acceder, rectificar, actualizar y suprimir tus datos, así como a oponerte a su
          tratamiento. Para ejercerlos, escribinos a
          <a href="mailto:privacidad@sartorinmobiliaria.com">privacidad@sartorinmobiliaria.com</a>.
          La <b>Agencia de Acceso a la Información Pública (AAIP)</b>, órgano de control de la
          Ley 25.326, atiende denuncias por incumplimiento.
        </p>
      </section>

      <section class="priv__sec">
        <h2>10. Menores</h2>
        <p>Nuestro sitio está dirigido a personas mayores de edad. No recopilamos deliberadamente datos de menores.</p>
      </section>

      <section class="priv__sec">
        <h2>11. Cambios a esta política</h2>
        <p>Podemos actualizar esta política. Publicaremos la versión vigente en esta página con su fecha de actualización.</p>
      </section>

      <section class="priv__sec">
        <h2>12. Contacto</h2>
        <p>
          Ante cualquier duda sobre esta política o el tratamiento de tus datos, escribinos a
          <a href="mailto:privacidad@sartorinmobiliaria.com">privacidad@sartorinmobiliaria.com</a>
          o usá el <a href="/#contact-section">formulario de contacto</a>.
        </p>
      </section>
    </div>
  </div>
</template>

<script setup>
useHead({
  title: 'Política de Privacidad — Sartor Inmobiliaria',
  meta: [{ name: 'description', content: 'Cómo Sartor Inmobiliaria recopila, usa y protege tus datos personales.' }],
});
</script>

<style scoped>
.priv { padding: 48px 20px 90px; }
.priv__wrap { max-width: 760px; margin: 0 auto; }
.priv__eyebrow { font-size: 11px; letter-spacing: .14em; text-transform: uppercase; color: #b52118; font-weight: 700; }
.priv__title { font-size: 34px; font-weight: 600; color: var(--sa-ink, #1a1814); margin: 8px 0 4px; }
.priv__updated { font-size: 13px; color: #a39a8c; margin: 0 0 24px; }
.priv__lead { font-size: 16px; line-height: 1.6; color: #5b544a; margin: 0 0 12px; }
.priv__sec { margin-top: 26px; }
.priv__sec h2 { font-size: 17px; font-weight: 600; color: var(--sa-ink); margin: 0 0 8px; }
.priv__sec p { font-size: 14.5px; line-height: 1.65; color: #5b544a; margin: 0 0 8px; }
.priv__sec ul { margin: 0 0 8px; padding-left: 20px; }
.priv__sec li { font-size: 14.5px; line-height: 1.6; color: #5b544a; margin-bottom: 6px; }
.priv__sec a { color: #b52118; text-decoration: none; }
.priv__sec a:hover { text-decoration: underline; }
.priv__sec code { background: #f4f1ea; border-radius: 4px; padding: 1px 5px; font-size: 13px; color: #8a1c14; }
</style>
